/**
 * Restaurant-Betriebsmodus:
 * - Zutaten (ingredient) = Inventaire + Achat → stock → Cuisine (ou POS nur wenn sellOnPos)
 * - Plats (restaurant_dish) = Inventaire direct → QuickSale sans achat ni stock
 * - POS-Resource: siehe domain/restaurant/restaurantPosCatalog.js
 */

import {
  RESTAURANT_MENU_CATEGORY,
  normalizeRestaurantDishSection
} from './restaurantMenuCatalog';

export const PRODUCT_ROLE_RESTAURANT_DISH = 'restaurant_dish';
export const PRODUCT_ROLE_INGREDIENT = 'ingredient';

export function getBusinessTypeId(businessType) {
  if (!businessType) return null;
  if (typeof businessType === 'string') return businessType;
  return businessType.id || businessType.key || null;
}

export function isRestaurantBusinessType(businessType) {
  return getBusinessTypeId(businessType) === 'restaurant';
}

export function isRestaurantDishProduct(product) {
  if (!product) return false;
  return (
    product.productRole === PRODUCT_ROLE_RESTAURANT_DISH ||
    product.isRestaurantDish === true
  );
}

export function isRestaurantIngredientProduct(product) {
  if (!product || isRestaurantDishProduct(product)) return false;
  if (product.productRole === PRODUCT_ROLE_INGREDIENT) return true;
  return !product.productRole;
}

export function getRestaurantQuickSaleProducts(products) {
  return (products || []).filter(isRestaurantDishProduct);
}

export function getRestaurantIngredientProducts(products) {
  return (products || []).filter(isRestaurantIngredientProduct);
}

export {
  RESTAURANT_POS_STOCK_SECTION,
  isRestaurantSellableStockProduct,
  isRestaurantPosResource,
  getRestaurantPosSaleProducts
} from '../domain/restaurant/restaurantPosCatalog';

/** Legacy restaurantDishes[] → products[] (einmalig beim Laden). */
export function migrateRestaurantDishesToProducts(dishes, existingProducts) {
  if (!Array.isArray(dishes) || dishes.length === 0) return [];
  const existingNames = new Set(
    (existingProducts || [])
      .filter(isRestaurantDishProduct)
      .map((p) => String(p.name || '').toLowerCase())
  );
  const migrated = [];
  for (const dish of dishes) {
    const name = String(dish.name || '').trim();
    if (!name || existingNames.has(name.toLowerCase())) continue;
    const section = dish.menuSection || dish.subcategory || 'other';
    migrated.push({
      id: dish.id || `dish_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      name,
      productRole: PRODUCT_ROLE_RESTAURANT_DISH,
      isRestaurantDish: true,
      trackInventory: false,
      category: RESTAURANT_MENU_CATEGORY,
      subcategory: section,
      menuSection: section,
      type: dish.type || section,
      unit: 'portion',
      conservation: 'SANS_DATE',
      picking: 'FIFO',
      sellingUnitPrice: Number(dish.price) || 0,
      sellingPrice: Number(dish.price) || 0,
      image: dish.image || dish.imageUrl || null,
      imageUrl: dish.imageUrl || dish.image || null,
      quantity: 0,
      sku: dish.sku || `PLAT${Date.now().toString().slice(-6)}`,
      createdAt: dish.createdAt || new Date().toISOString(),
      migratedFromRestaurantDishes: true
    });
    existingNames.add(name.toLowerCase());
  }
  return migrated;
}

/** Anciens plats (food-beverages / restaurant-menu) → structure menu. */
export function normalizeLegacyRestaurantDishProducts(products) {
  if (!Array.isArray(products) || products.length === 0) return products;
  let changed = false;
  const normalized = products.map((product) => {
    if (!isRestaurantDishProduct(product)) return product;
    const section = normalizeRestaurantDishSection(product);
    const updates = {};
    if (product.category !== RESTAURANT_MENU_CATEGORY) {
      updates.category = RESTAURANT_MENU_CATEGORY;
    }
    if (product.subcategory !== section) {
      updates.subcategory = section;
    }
    if (product.menuSection !== section) {
      updates.menuSection = section;
    }
    if (product.type === 'dish' || !product.type) {
      updates.type = section;
    }
    if (Object.keys(updates).length === 0) return product;
    changed = true;
    return { ...product, ...updates };
  });
  return changed ? normalized : products;
}

/** Migration + Normalisierung beim Laden eines Restaurant-Betriebs. */
export function prepareRestaurantProductsOnLoad(businessType, products, restaurantDishes) {
  let result = Array.isArray(products) ? products : [];
  let persistProducts = false;
  let clearRestaurantDishes = false;

  if (
    isRestaurantBusinessType(businessType) &&
    Array.isArray(restaurantDishes) &&
    restaurantDishes.length > 0
  ) {
    const migrated = migrateRestaurantDishesToProducts(restaurantDishes, result);
    if (migrated.length > 0) {
      result = [...result, ...migrated];
      persistProducts = true;
      clearRestaurantDishes = true;
    }
  }
  if (isRestaurantBusinessType(businessType)) {
    const normalized = normalizeLegacyRestaurantDishProducts(result);
    if (normalized !== result) {
      result = normalized;
      persistProducts = true;
    }
  }
  return { products: result, persistProducts, clearRestaurantDishes };
}

export function normalizeBusinessTypeForConfig(businessType) {
  const id = getBusinessTypeId(businessType);
  if (!id) return businessType;
  if (typeof businessType === 'object' && businessType.id) return businessType;
  return { id };
}

export {
  isRestaurantDishSale,
  filterRestaurantDishSales,
  getSaleTotalAmount,
  getRestaurantDishSaleQuantity,
  summarizeRestaurantDishSales,
  summarizeSalesByType,
  RESTAURANT_SALE_TYPE
} from '../domain/restaurant/restaurantDishSaleService';
